/**
 * Users & Roles — `docs/html/admin.html#users` ported.
 *
 * **This screen talks to the real API.** `docs/API.md` §3 is implemented, so
 * everything here is live: the list, creating a user, changing status, granting
 * and revoking roles, and the permission check. No fixtures.
 *
 * Two behaviours are worth knowing before reading the code:
 *
 *  - Granting a role returns an `effect_preview` — the screens and operations
 *    the person gains. It is shown after the grant because it answers the
 *    question an administrator actually has, which a bare confirmation does not.
 *  - Editing carries `row_version`. A concurrent edit comes back as
 *    `409 stale_write` with the current record attached, and the UI offers to
 *    reload rather than silently overwriting a colleague.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as usersApi from '../../api/users.api.js';
import * as rolesApi from '../../api/roles.api.js';
import { usePartners } from '../partners/usePartners.js';
import { queryKeys } from '../../app/queryClient.js';
import { useCursorPages } from '../../hooks/useCursorPages.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useAuth } from '../../hooks/useAuth.js';
import { pushToast } from '../ui/uiSlice.js';
import { DataTable } from '../../components/common/DataTable.jsx';
import {
  PageHeader, Card, Badge, Avatar, SearchInput, ChipSelect, Tabs,
  Drawer, Modal, Field, Spinner, ErrorState, EmptyState,
} from '../../components/common/index.jsx';
import { date, dateTime } from '../../utils/formatters.js';
import RolesAdminPanel from './RolesAdminPanel.jsx';

const STATUS_TONE = {
  active: 'emerald', invited: 'sky', suspended: 'amber',
  disabled: 'neutral', locked: 'rose',
};

export default function UsersAdminPage() {
  const [tab, setTab] = useState('users');

  return (
    <>
      <PageHeader title="Users & roles" subtitle="Who has access, and what that access means." />
      <Card bodyClass="p-0">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[{ key: 'users', label: 'Users' }, { key: 'roles', label: 'Roles & permissions' }]}
        />
        <div className="p-3">
          {tab === 'users' ? <UsersPanel /> : <RolesAdminPanel />}
        </div>
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Users                                                                       */
/* -------------------------------------------------------------------------- */

function UsersPanel() {
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [role, setRole] = useState('');
  const [openUserId, setOpenUserId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const debounced = useDebounce(search);
  const pages = useCursorPages(25);

  const params = useMemo(
    () => ({ ...pages.params, q: debounced || undefined, status: status || undefined, role: role || undefined }),
    [pages.params, debounced, status, role],
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: () => usersApi.listUsers(params),
    placeholderData: (previous) => previous,
  });

  const roles = useQuery({ queryKey: queryKeys.roles.list(), queryFn: rolesApi.listRoles });

  const onFilter = (setter) => (value) => { setter(value); pages.reset(); };

  const columns = [
    {
      key: 'full_name',
      header: 'User',
      render: (u) => (
        <div className="d-flex align-items-center gap-2">
          <Avatar name={u.full_name} size={32} />
          <div>
            <div className="fw-600">{u.full_name}</div>
            <div className="t-xs text-muted">{u.email}</div>
          </div>
        </div>
      ),
    },
    { key: 'designation', header: 'Designation', render: (u) => u.designation ?? <span className="text-muted">—</span> },
    {
      key: 'roles',
      header: 'Roles',
      render: (u) =>
        u.roles?.length ? (
          <div className="d-flex flex-wrap gap-1">
            {u.roles.map((r) => (
              <Badge key={r.id} tone={r.source === 'sso_claim' ? 'violet' : 'neutral'} title={r.source}>
                {r.code}{r.scope ? ` · ${r.scope.name}` : ''}
              </Badge>
            ))}
          </div>
        ) : (
          // A user with no role can sign in and see nothing, which looks like a
          // broken system rather than a pending setup step. Worth flagging here.
          <Badge tone="amber">No role</Badge>
        ),
    },
    { key: 'status', header: 'Status', width: 110, render: (u) => <Badge tone={STATUS_TONE[u.status] ?? 'neutral'}>{u.status}</Badge> },
    {
      key: 'last_login_at',
      header: 'Last sign-in',
      width: 150,
      render: (u) => (u.last_login_at ? <span className="t-xs">{dateTime(u.last_login_at)}</span> : <span className="text-muted t-xs">Never</span>),
    },
  ];

  return (
    <>
      <div className="d-flex flex-wrap gap-2 justify-content-between mb-3">
        <div className="filter-bar flex-grow-1 p-0 border-0">
          <SearchInput value={search} onChange={onFilter(setSearch)} placeholder="Search name, email or employee code…" className="flex-grow-1" />
          <ChipSelect label="Status" allLabel="All statuses" value={status} onChange={onFilter(setStatus)} options={Object.keys(STATUS_TONE)} />
          <ChipSelect
            label="Role"
            allLabel="All roles"
            value={role}
            onChange={onFilter(setRole)}
            options={(roles.data ?? []).map((r) => ({ value: r.code, label: r.name }))}
          />
        </div>
        {can('admin.users', 'create') && (
          <button type="button" className="btn btn-add btn-sm-x" onClick={() => setCreateOpen(true)}>
            <i className="bi bi-person-plus me-1" aria-hidden="true" />Invite user
          </button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        isLoading={isLoading}
        error={error}
        onRetry={refetch}
        onRowClick={(u) => setOpenUserId(u.id)}
        page={data?.page}
        onNext={pages.goNext}
        onPrevious={pages.goPrevious}
        canGoPrevious={pages.canGoPrevious}
        emptyTitle="No users match these filters"
      />

      <UserDrawer userId={openUserId} onClose={() => setOpenUserId(null)} roles={roles.data ?? []} />
      <InviteUserModal open={createOpen} onClose={() => setCreateOpen(false)} roles={roles.data ?? []} />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* User detail                                                                 */
/* -------------------------------------------------------------------------- */

/** Profile, role grants and status, in a drawer so the list keeps its place. */
function UserDrawer({ userId, onClose, roles }) {
  const { can } = useAuth();
  const [tab, setTab] = useState('profile');

  const { data: user, isLoading } = useQuery({
    queryKey: queryKeys.users.detail(userId),
    queryFn: () => usersApi.getUser(userId),
    enabled: Boolean(userId),
  });

  return (
    <Drawer
      open={Boolean(userId)}
      onClose={onClose}
      title={user?.full_name ?? 'User'}
      subtitle={user?.email}
      width={640}
    >
      {isLoading || !user ? (
        <Spinner />
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={setTab}
            tabs={[
              { key: 'profile', label: 'Profile' },
              { key: 'roles', label: 'Roles', badge: user.roles?.length ?? 0 },
              { key: 'access', label: 'Access check' },
            ]}
          />
          <div className="pt-3">
            {tab === 'profile' && <UserProfileTab user={user} canEdit={can('admin.users', 'edit')} />}
            {tab === 'roles' && <UserRolesTab user={user} roles={roles} />}
            {tab === 'access' && <AccessCheckTab userId={user.id} />}
          </div>
        </>
      )}
    </Drawer>
  );
}

/** Read-only profile plus the status control. */
function UserProfileTab({ user, canEdit }) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [statusOpen, setStatusOpen] = useState(false);

  const changeStatus = useMutation({
    mutationFn: (payload) => usersApi.setUserStatus(user.id, payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      dispatch(pushToast({
        tone: 'success',
        message: `Status set to ${result.status}`,
        // Disabling revokes sessions immediately; saying how many makes that concrete.
        detail: result.sessions_revoked > 0 ? `${result.sessions_revoked} session(s) ended.` : undefined,
      }));
      setStatusOpen(false);
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not change status', detail: error.message })),
  });

  return (
    <>
      <dl className="detail-grid mb-3">
        <div className="detail-row"><dt>Employee code</dt><dd>{user.employee_code ?? '—'}</dd></div>
        <div className="detail-row"><dt>Designation</dt><dd>{user.designation ?? '—'}</dd></div>
        <div className="detail-row"><dt>Department</dt><dd>{user.department?.name ?? '—'}</dd></div>
        <div className="detail-row"><dt>Manager</dt><dd>{user.manager?.full_name ?? '—'}</dd></div>
        <div className="detail-row"><dt>Team</dt><dd>{user.primary_team?.name ?? '—'}</dd></div>
        <div className="detail-row"><dt>Territory</dt><dd>{user.territory?.name ?? '—'}</dd></div>
        <div className="detail-row"><dt>Mobile</dt><dd>{user.mobile ?? '—'}</dd></div>
        <div className="detail-row"><dt>Timezone</dt><dd>{user.timezone}</dd></div>
        <div className="detail-row"><dt>Locale</dt><dd>{user.locale}</dd></div>
        <div className="detail-row"><dt>Joined</dt><dd>{user.joining_date ? date(user.joining_date) : '—'}</dd></div>
        <div className="detail-row"><dt>Licensed</dt><dd>{user.is_licensed ? 'Yes' : 'No'}</dd></div>
        <div className="detail-row"><dt>MFA enforced</dt><dd>{user.mfa_enforced ? 'Yes' : 'No'}</dd></div>
        <div className="detail-row">
          <dt>Sign-in methods</dt>
          <dd>{user.auth_methods?.length ? user.auth_methods.join(', ') : 'None set'}</dd>
        </div>
        <div className="detail-row"><dt>Record version</dt><dd><code>{user.row_version}</code></dd></div>
      </dl>

      {canEdit && (
        <div className="d-flex gap-2">
          <button type="button" className="btn-x btn-ghost" onClick={() => setStatusOpen(true)}>
            <i className="bi bi-toggle-on me-1" aria-hidden="true" />Change status
          </button>
        </div>
      )}

      <StatusModal
        open={statusOpen}
        user={user}
        busy={changeStatus.isPending}
        onClose={() => setStatusOpen(false)}
        onConfirm={(payload) => changeStatus.mutate(payload)}
      />
    </>
  );
}

/**
 * Status change.
 *
 * A reason is mandatory for anything that restricts access — the API enforces
 * it, and it ends up in the audit trail, which is where someone will look in six
 * months asking why this account was suspended.
 */
function StatusModal({ open, user, busy, onClose, onConfirm }) {
  const [status, setStatus] = useState('suspended');
  const [reason, setReason] = useState('');
  const needsReason = status !== 'active';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change status"
      footer={
        <>
          <button type="button" className="btn btn-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-save"
            disabled={busy || (needsReason && !reason.trim())}
            onClick={() => onConfirm({ status, reason: reason.trim() || undefined })}
          >
            {busy ? 'Saving…' : 'Apply'}
          </button>
        </>
      }
    >
      <p className="t-sm">
        {user?.full_name} is currently <strong>{user?.status}</strong>.
      </p>
      <Field label="New status" required>
        <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="disabled">Disabled</option>
        </select>
      </Field>
      <Field
        label="Reason"
        required={needsReason}
        help={needsReason ? 'Recorded in the audit trail. Anything other than Active ends every session this user holds, immediately.' : undefined}
      >
        <textarea className="form-control" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Role grants                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Grant and revoke roles.
 *
 * Revoking sets `revoked_at` rather than deleting — an expired permission is
 * evidence. A grant whose source is `sso_claim` came from the directory and will
 * be re-created on next sign-in, so revoking it by hand is marked as futile
 * rather than silently undone later.
 */
function UserRolesTab({ user, roles }) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [preview, setPreview] = useState(null);
  const [roleId, setRoleId] = useState('');
  const [scopeTeam, setScopeTeam] = useState('');
  const [reason, setReason] = useState('');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(user.id) });
    queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
  };

  const grant = useMutation({
    mutationFn: () =>
      usersApi.grantRole(user.id, {
        role_id: roleId,
        scope_team_id: scopeTeam || undefined,
        reason: reason.trim() || undefined,
      }),
    onSuccess: (result) => {
      invalidate();
      setRoleId(''); setScopeTeam(''); setReason('');
      setPreview(result);
    },
    onError: (error) => {
      dispatch(pushToast({
        tone: 'danger',
        message: error.code === 'privilege_escalation'
          ? 'You cannot grant a role carrying permissions you do not hold'
          : 'Could not grant that role',
        detail: error.message,
      }));
    },
  });

  const revoke = useMutation({
    mutationFn: (grantId) => usersApi.revokeRole(user.id, grantId),
    onSuccess: () => {
      invalidate();
      dispatch(pushToast({ tone: 'success', message: 'Role revoked' }));
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not revoke', detail: error.message })),
  });

  return (
    <>
      {user.roles?.length ? (
        <ul className="list-x mb-3">
          {user.roles.map((g) => (
            <li key={g.id} className="list-x-item">
              <div className="flex-grow-1">
                <div className="fw-600 t-sm">{g.name ?? g.code}</div>
                <div className="t-xs text-muted">
                  {g.scope ? `${g.scope.kind}: ${g.scope.name}` : 'Unscoped'}
                  {' · '}source {g.source}
                  {g.valid_to && ` · until ${date(g.valid_to)}`}
                </div>
              </div>
              {g.is_primary && <Badge tone="brand">Primary</Badge>}
              {g.source === 'sso_claim' && <Badge tone="violet" title="Granted by the identity provider">SSO</Badge>}
              <button
                type="button"
                className="btn btn-delete btn-sm-x"
                onClick={() => revoke.mutate(g.id)}
                disabled={revoke.isPending}
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="bi-shield-slash" title="No roles granted" message="This person can sign in but will see nothing." />
      )}

      <Card title="Grant a role">
        <Field label="Role" required>
          <select className="form-select" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">Choose a role…</option>
            {roles.filter((r) => r.is_assignable).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}{r.is_system ? ' (template)' : ''} — scope {r.default_scope}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reason" help="Recorded on the grant and in the audit trail.">
          <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Covering Marek during parental leave" />
        </Field>
        <button type="button" className="btn btn-add" disabled={!roleId || grant.isPending} onClick={() => grant.mutate()}>
          {grant.isPending ? 'Granting…' : 'Grant role'}
        </button>
      </Card>

      <EffectPreviewModal preview={preview} onClose={() => setPreview(null)} />
    </>
  );
}

/**
 * What the grant actually changed.
 *
 * Shown after the grant rather than before, because the API computes it against
 * the user's real current permissions — which is the only way to answer "what is
 * *new* for this person" rather than "what does this role contain".
 */
function EffectPreviewModal({ preview, onClose }) {
  const effect = preview?.effect_preview;

  return (
    <Modal
      open={Boolean(preview)}
      onClose={onClose}
      title="Role granted"
      footer={<button type="button" className="btn-x btn-primary-x" onClick={onClose}>Done</button>}
    >
      <p className="t-sm">
        <strong>{preview?.name ?? preview?.code}</strong> granted
        {preview?.scope ? ` at ${preview.scope.kind} scope (${preview.scope.name})` : ''}.
      </p>

      {effect?.screens_gained?.length > 0 && (
        <>
          <h3 className="card-x-title mt-3 mb-2">Screens now visible</h3>
          <div className="d-flex flex-wrap gap-1">
            {effect.screens_gained.map((s) => <Badge key={s} tone="emerald">{s}</Badge>)}
          </div>
        </>
      )}

      {effect?.data_scope_changes?.length > 0 && (
        <>
          <h3 className="card-x-title mt-3 mb-2">Data scope widened</h3>
          <ul className="t-sm mb-0">
            {effect.data_scope_changes.map((c) => (
              <li key={c.screen}>{c.screen}: {c.from} → <strong>{c.to}</strong></li>
            ))}
          </ul>
        </>
      )}

      {!effect?.screens_gained?.length && !effect?.data_scope_changes?.length && (
        <p className="t-sm text-muted mb-0">
          This role adds nothing the person did not already have — another of their roles
          already covers it.
        </p>
      )}
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Access check                                                                */
/* -------------------------------------------------------------------------- */

/**
 * "Can this person do X?" — the test-as-user panel.
 *
 * The value is in `denied_by`: it names the role that is blocking. Without that,
 * an administrator debugging a permission complaint is guessing which of four
 * roles is responsible.
 */
function AccessCheckTab({ userId }) {
  const [checks, setChecks] = useState([
    { screen: 'leads', operation: 'view' },
    { screen: 'quotations', operation: 'approve' },
    { screen: 'admin.users', operation: 'edit' },
  ]);
  const [screen, setScreen] = useState('');
  const [operation, setOperation] = useState('view');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.users.permissionCheck(userId, checks),
    queryFn: () => usersApi.checkPermissions(userId, checks),
    enabled: checks.length > 0,
  });

  const catalogue = useQuery({ queryKey: queryKeys.roles.catalogue, queryFn: rolesApi.getCatalogue });

  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <Field label="Screen" className="flex-grow-1 mb-0">
          <select className="form-select" value={screen} onChange={(e) => setScreen(e.target.value)}>
            <option value="">Choose…</option>
            {(catalogue.data?.screens ?? []).map((s) => (
              <option key={s.key} value={s.key}>{s.key} — {s.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Operation" className="mb-0">
          <select className="form-select" value={operation} onChange={(e) => setOperation(e.target.value)}>
            {(catalogue.data?.operations ?? []).map((o) => (
              <option key={o.key} value={o.key}>{o.key}</option>
            ))}
          </select>
        </Field>
        <button
          type="button"
          className="btn btn-add"
          disabled={!screen}
          onClick={() => setChecks((c) => [...c, { screen, operation }])}
        >
          Add check
        </button>
      </div>

      {isLoading ? <Spinner /> : error ? <ErrorState error={error} onRetry={refetch} /> : (
        <div className="table-x-wrap">
          <table className="table-x is-dense">
            <thead>
              <tr>
                <th>Screen</th>
                <th>Operation</th>
                <th>Allowed</th>
                <th>Scope</th>
                <th>Decided by</th>
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((result, i) => (
                <tr key={`${result.screen}-${result.operation}-${i}`}>
                  <td><code className="t-xs">{result.screen}</code></td>
                  <td>{result.operation}</td>
                  <td>
                    <Badge tone={result.allowed ? 'emerald' : 'rose'}>
                      {result.allowed ? 'Allowed' : 'Denied'}
                    </Badge>
                  </td>
                  <td>{result.data_scope}</td>
                  <td className="t-xs">
                    {result.allowed
                      ? result.granted_by.join(', ') || '—'
                      : result.reason === 'unknown_screen_or_operation'
                        ? <span className="text-muted">No such screen or operation</span>
                        : result.denied_by.length
                          ? <>denied by <strong>{result.denied_by.join(', ')}</strong></>
                          : <span className="text-muted">{result.reason}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Invite                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Create a user and send an invitation.
 *
 * Roles are chosen here rather than afterwards because a user with no role signs
 * in and sees nothing — which reads as a broken system rather than a setup step
 * someone has not finished.
 */
function InviteUserModal({ open, onClose, roles }) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [form, setForm] = useState({ full_name: '', email: '', designation: '', role_id: '', partner_id: '' });
  const [fieldErrors, setFieldErrors] = useState({});

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  /* A user's role is stored as role_id, but whether the extra "which partner
     company" question applies depends on the role's code — see
     docs/modules/partnerLeads.model.md §3. */
  const isPartnerRole = roles.find((r) => r.id === form.role_id)?.code === 'partner';
  const partners = usePartners({ is_active: true, limit: 200 });
  const partnerOptions = partners.data?.data ?? [];

  const create = useMutation({
    mutationFn: () =>
      usersApi.createUser({
        full_name: form.full_name,
        email: form.email,
        designation: form.designation || undefined,
        partner_id: isPartnerRole && form.partner_id ? form.partner_id : undefined,
        roles: form.role_id ? [{ role_id: form.role_id, is_primary: true }] : [],
        send_invitation: true,
      }),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      dispatch(pushToast({
        tone: 'success',
        message: `Invited ${user.full_name}`,
        // No mail transport is configured server-side, so be honest about it.
        detail: 'No mail transport is configured — the invitation link is in the API server log.',
      }));
      setForm({ full_name: '', email: '', designation: '', role_id: '', partner_id: '' });
      onClose();
    },
    onError: (error) => {
      setFieldErrors(error.fieldErrors?.() ?? {});
      if (!error.isValidation) {
        dispatch(pushToast({ tone: 'danger', message: 'Could not create the user', detail: error.message }));
      }
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite a user"
      footer={
        <>
          <button type="button" className="btn btn-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-add"
            disabled={!form.full_name || !form.email || (isPartnerRole && !form.partner_id) || create.isPending}
            onClick={() => { setFieldErrors({}); create.mutate(); }}
          >
            {create.isPending ? 'Inviting…' : 'Send invitation'}
          </button>
        </>
      }
    >
      <Field label="Full name" required error={fieldErrors.full_name}>
        <input className="form-control" value={form.full_name} onChange={set('full_name')} />
      </Field>
      <Field label="Email" required error={fieldErrors.email}>
        <input type="email" className="form-control" value={form.email} onChange={set('email')} />
      </Field>
      <Field label="Designation" error={fieldErrors.designation}>
        <input className="form-control" value={form.designation} onChange={set('designation')} />
      </Field>
      <Field label="Role" help="A user with no role can sign in but will see an empty application.">
        <select className="form-select" value={form.role_id} onChange={set('role_id')}>
          <option value="">No role yet</option>
          {roles.filter((r) => r.is_assignable).map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
      </Field>
      {isPartnerRole && (
        <Field label="Partner company" required error={fieldErrors.partner_id}
               help="Which partner company this login belongs to — see docs/modules/partnerLeads.model.md.">
          <select className="form-select" value={form.partner_id} onChange={set('partner_id')}>
            <option value="">Select a partner…</option>
            {partnerOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      )}
    </Modal>
  );
}
